import { MapboxSearchAPI } from './MapboxSearchAPI'
import type { LocationWizardData } from '../search-wizard/domain/LocationWizardData'

/**
 * Resolve a text input to a LocationWizardData via the Mapbox geocode hybrid
 * endpoint. Returns null if no match is found.
 */
export async function geocodeLocation(
  langCode: string,
  sessionId: string,
  inputValue: string,
): Promise<LocationWizardData | null> {
  try {
    const mapboxSearchAPI = new MapboxSearchAPI(langCode, sessionId)

    const suggestions = await mapboxSearchAPI.get(inputValue, {
      geocodeMode: true,
    })
    if (!suggestions.length) return null

    const firstSuggestion = suggestions[0]
    if (!firstSuggestion) return null

    const retrieveResponse = await mapboxSearchAPI.getPointForSuggestion(
      firstSuggestion.mapboxId,
    )

    const { coordinates } = retrieveResponse.properties

    return {
      dataType: 'custom',
      name: firstSuggestion.name,
      mapboxId: firstSuggestion.mapboxId,
      point: {
        lat: coordinates.latitude,
        lng: coordinates.longitude,
        isEmptyPoint: false,
      },
    }
  } catch (error) {
    console.warn('geocodeLocation: failed to resolve', inputValue, error)
    return null
  }
}

/**
 * Geocode fallback flow for when the autosuggest dropdown is dismissed
 * without a selection. Resolves the input text via Mapbox and notifies
 * the provided callbacks.
 *
 * Returns true if a location was resolved, false otherwise.
 */
export async function resolveGeocodeFallback(
  inputValue: string,
  langCode: string,
  sessionId: string,
  callbacks: {
    onSelecting: (name: string) => void
    onResolved: (location: LocationWizardData) => void
  },
): Promise<boolean> {
  callbacks.onSelecting(inputValue)
  const location = await geocodeLocation(langCode, sessionId, inputValue)
  if (!location) return false
  callbacks.onResolved(location)
  return true
}
